/**
 * Follow-mode helpers: pure decisions for presence + "follow a teammate across
 * pages". The DOM/provider wiring lives in the embed controller; everything
 * here is side-effect free and unit-tested.
 */

/** How to move the host page to a teammate's location. */
export type FollowNavigationKind = "none" | "hash" | "soft" | "full";

const USER_ID_KEY = "magic_comment_user_id";

interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Return the browser-stable user id, generating and persisting one on first
 * use. A stable id (unlike the per-connection awareness client id) lets us keep
 * following a teammate across their reconnects and our own soft navigations.
 */
export function ensureUserId(
  storage: KeyValueStore,
  generate: () => string,
): string {
  const existing = storage.getItem(USER_ID_KEY);
  if (existing) return existing;
  const id = generate();
  storage.setItem(USER_ID_KEY, id);
  return id;
}

/** The page a teammate is currently viewing, broadcast over awareness. */
export interface PresencePage {
  pageId: string;
  url: string;
  title?: string;
}

/** A remote teammate visible in the presence bar. */
export interface PresencePeer {
  /** Awareness client id (per-connection, changes on reconnect). */
  clientId: number;
  /** Stable browser-scoped user id, used to follow across reconnects. */
  id?: string;
  name: string;
  color: string;
  avatarUrl?: string;
  pageId: string;
  url: string;
  title?: string;
}

interface AwarenessUser {
  id?: string;
  name?: string;
  color?: string;
  avatarUrl?: string;
}

/** The four corners the draggable widget FAB can dock to. */
export type FabCorner =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

/**
 * Which side the presence bar should dock to, given where the FAB/toolbar is.
 * The bar lives at the top of the viewport, so it only collides with the
 * toolbar when the FAB is docked top-right — in that case move it to the left.
 * Every other corner leaves the top-right free, so it stays on the right.
 */
export function presenceBarSide(corner: FabCorner): "left" | "right" {
  return corner === "top-right" ? "left" : "right";
}

/** What the follower should do this tick. */
export type FollowAction =
  | { action: "navigate"; target: string }
  | { action: "none" }
  | { action: "wait" };

/**
 * Decide the follower's next move given the followed peer (or null when they
 * are not currently visible) and our current location.
 *
 * Crucially, a null peer yields "wait" — NOT a stop. A followed teammate that
 * navigates briefly drops out of awareness while their widget reconnects; we
 * keep following and resume once they re-announce (same stable id). Follow is
 * only ended by the user explicitly, never by a transient absence.
 */
export function decideFollowAction(
  peer: PresencePeer | null,
  currentHref: string,
): FollowAction {
  if (!peer) return { action: "wait" };
  if (peer.url === currentHref) return { action: "none" };
  return { action: "navigate", target: peer.url };
}

/**
 * Find the peer we are currently following by their stable user id. Returns
 * null when nobody matches (e.g. the followed teammate is momentarily absent
 * while reconnecting); the caller keeps following and waits for them.
 */
export function resolveFollowedPeer(
  peers: PresencePeer[],
  followId: string | null | undefined,
): PresencePeer | null {
  if (!followId) return null;
  return peers.find((peer) => peer.id === followId) ?? null;
}

/**
 * Turn raw awareness states into the list of followable peers: every remote
 * client (never the local one) that has broadcast both a user identity and the
 * page it is on.
 */
export function derivePresencePeers(
  states: Map<number, Record<string, unknown>>,
  localClientId: number,
): PresencePeer[] {
  const peers: PresencePeer[] = [];
  states.forEach((state, clientId) => {
    if (clientId === localClientId) return;
    const user = state.user as AwarenessUser | undefined;
    const page = state.page as PresencePage | undefined;
    if (!user?.name || !user.color || !page?.pageId || !page.url) return;
    peers.push({
      clientId,
      id: user.id,
      name: user.name,
      color: user.color,
      avatarUrl: user.avatarUrl,
      pageId: page.pageId,
      url: page.url,
      title: page.title,
    });
  });
  return peers;
}

/**
 * Collapse peers to one entry per person: keyed by the stable user id, falling
 * back to the per-connection client id when a peer has no id. The first
 * occurrence wins, so a teammate with two tabs open shows a single avatar.
 */
export function dedupePeersById(peers: PresencePeer[]): PresencePeer[] {
  const seen = new Set<string>();
  const result: PresencePeer[] = [];
  for (const peer of peers) {
    const key = peer.id ?? `client:${peer.clientId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(peer);
  }
  return result;
}

/**
 * Decide how to navigate from `currentHref` to `targetHref`:
 * - "none": already there.
 * - "hash": same document, only the fragment differs — set location.hash.
 * - "soft": same origin, different path — try SPA push-state first.
 * - "full": different origin — a full document navigation is required.
 */
export function planFollowNavigation(
  currentHref: string,
  targetHref: string,
): FollowNavigationKind {
  const current = new URL(currentHref);
  const target = new URL(targetHref);

  if (current.href === target.href) {
    return "none";
  }
  if (current.origin !== target.origin) {
    return "full";
  }
  if (
    current.pathname === target.pathname &&
    current.search === target.search
  ) {
    return "hash";
  }
  return "soft";
}
